"use client";

/**
 * The listing page's map: this listing and the cheaper or similar ones
 * nearby, on MapLibre with OpenFreeMap's free tiles (no token, no
 * per-view cost), taller than the picture it replaces, with a legend
 * that says what the dots are and a link to the full map (owner,
 * 2026-10-11: "the map is really short, I don't know what I see, and
 * do not use Mapbox here").
 */
import Link from "@/lib/nav-link";
import { LINK } from "@/lib/ui-classes";
import { ExternalLink } from "lucide-react";
import { DOT_TONE, type MapDot } from "@/components/listings/map-dots";
import LazyMiniMap from "@/components/listings/LazyMiniMap";


export default function ListingAreaMap({ lat, lng, dots, href, labels, className = "", zoom, legend = true }: {
  lat: number;
  lng: number;
  dots: MapDot[];
  /** The full map, centred here; none hides the link. */
  href?: string | null;
  labels: { thisOne: string; deal: string; fair: string; over: string; other: string; open: string };
  className?: string;
  zoom?: number;
  /** False hides the legend row (the land result: one dot needs no key). */
  legend?: boolean;
}) {
  const tones = new Set(dots.map((d) => d.tone ?? "none"));
  const legendRows: Array<[string, string]> = [
    ["this", labels.thisOne],
    ...(tones.has("deal") ? [["deal", labels.deal] as [string, string]] : []),
    ...(tones.has("fair") ? [["fair", labels.fair] as [string, string]] : []),
    ...(tones.has("over") ? [["over", labels.over] as [string, string]] : []),
    ...(tones.has("none") ? [["none", labels.other] as [string, string]] : []),
  ];
  return (
    <div className={className}>
      <LazyMiniMap lat={lat} lng={lng} interactive tall dots={dots} zoom={zoom} />
      {(legend || href) && <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-600">
        {legend && legendRows.map(([k, label]) => (
          <span key={k} className="inline-flex items-center gap-1.5">
            <span
              className={k === "this" ? "h-3 w-3 rounded-full border-2 border-white bg-indigo-700 shadow-sm" : "h-2.5 w-2.5 rounded-full border border-white shadow-sm"}
              style={k === "this" ? undefined : { background: DOT_TONE[k] }}
              aria-hidden
            />
            {label}
          </span>
        ))}
        {href && (
          <Link href={href} className={`ml-auto inline-flex items-center gap-1 ${LINK}`}>
            {labels.open}
            <ExternalLink className="h-3 w-3" aria-hidden />
          </Link>
        )}
      </div>}
    </div>
  );
}
